<template>
  <v-container>
    <v-card>
      <v-toolbar
        color="bg-primary-lighten-1"
        density="compact"
        class="px-5 py-3"
      >
        <v-icon
          icon="mdi-package-variant-closed"
          size="large"
          class="me-2"
        ></v-icon>
        <v-toolbar-title class="text-h5 font-weight-medium">
          {{ $t('packages.title') }}
        </v-toolbar-title>
      </v-toolbar>

      <v-card-text>
        <div class="d-flex align-center filters-wrapper">
          <v-text-field
            v-model="search"
            :placeholder="$t('packages.search')"
            prepend-inner-icon="mdi-magnify"
            variant="outlined"
            hide-details
            class="table-search me-2"
            @update:model-value="debouncedSearch"
          ></v-text-field>
          <v-select
            v-model="selectedStatus"
            :items="packageStatuses"
            item-title="title"
            item-value="value"
            :label="$t('packages.statusFilter')"
            variant="outlined"
            hide-details
            clearable
            class="table-filter me-2"
            @update:model-value="fetchPackages"
          ></v-select>
          <v-select
            v-model="selectedType"
            :items="packageTypes"
            item-title="title"
            item-value="value"
            :label="$t('packages.typeFilter')"
            variant="outlined"
            hide-details
            clearable
            class="table-filter"
            @update:model-value="fetchPackages"
          ></v-select>
        </div>
      </v-card-text>

      <!-- Table -->
      <v-data-table
        :headers="headers"
        :items="packages"
        :loading="loading"
        :items-per-page="itemsPerPage"
        :items-per-page-options="[]"
        hide-default-footer
        class="custom-table elevation-1"
        hover
        @click:row="navigateToPackageDetails"
        v-model:sort-by="sortBy"
        v-model:sort-desc="sortDesc"
        @update:sort-by="fetchPackages"
        @update:sort-desc="fetchPackages"
      >
        <!-- Device column -->
        <template v-slot:item.device_sn="{ item }">
          <span class="text-truncate" style="max-width: 100px">
            {{ item.device_sn ? `...${item.device_sn.slice(-4)}` : 'N/A' }}
          </span>
        </template>

        <!-- Cell column -->
        <template v-slot:item.cell_number="{ item }">
          <span class="text-truncate" style="max-width: 100px">
            {{ item.cell_number || 'N/A' }}
          </span>
        </template>

        <!-- Sender column -->
        <template v-slot:item.sender="{ item }">
          <div class="d-flex align-center">
            <div class="user-info" style="max-width: 150px">
                                    <div class="user-name-line">{{ getUserName(item.sender) }}</div>
                      <div v-if="getUserPhone(item.sender)" class="user-phone-line">
                        {{ getUserPhone(item.sender) }}
                <v-btn
                  v-if="getUserPhone(item.sender)"
                  icon="mdi-content-copy"
                  variant="text"
                  size="x-small"
                  density="compact"
                  @click.stop="copyToClipboard(getUserPhone(item.sender), $t('packages.phone'))"
                ></v-btn>
              </div>
            </div>
          </div>
        </template>

        <!-- Recipient column -->
        <template v-slot:item.recipient="{ item }">
          <div class="d-flex align-center">
            <div class="user-info" style="max-width: 150px">
                                    <div class="user-name-line">{{ getUserName(item.recipient) }}</div>
                      <div v-if="getUserPhone(item.recipient)" class="user-phone-line">
                        {{ getUserPhone(item.recipient) }}
                <v-btn
                  v-if="getUserPhone(item.recipient)"
                  icon="mdi-content-copy"
                  variant="text"
                  size="x-small"
                  density="compact"
                  @click.stop="copyToClipboard(getUserPhone(item.recipient), $t('packages.phone'))"
                ></v-btn>
              </div>
            </div>
          </div>
        </template>

        <!-- Created At column -->
        <template v-slot:item.created_at="{ item }">
          {{ formatCreatedAt(item.created_at) }}
        </template>

        <!-- Status column -->
        <template v-slot:item.status="{ item }">
          <v-chip
            :color="getStatusColor(item.status)"
            :class="getStatusClass(item.status)"
            size="small"
          >
            {{ item.status }}
          </v-chip>
        </template>

        <!-- Type column -->
        <template v-slot:item.parcel_type="{ item }">
          <v-chip
            :color="getTypeColor(item.parcel_type)"
            :class="getTypeClass(item.parcel_type)"
            size="small"
          >
            {{ item.parcel_type || 'N/A' }}
          </v-chip>
        </template>
      </v-data-table>
      
      <!-- Pagination -->
      <base-pagination
        v-model:current-page="page"
        v-model:items-per-page="itemsPerPage"
        :total-items="totalItems"
        :items-per-page-options="[10, 25, 50, 100]"
      />
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import api from '@/services/api'
import debounce from 'lodash/debounce'
import { formatDistanceToNow, format, isToday } from 'date-fns'
import { enUS, ru, vi } from 'date-fns/locale'
import { useSnackbar } from '@/composables/useSnackbar'
import { useRouter } from 'vue-router'
import BasePagination from '@/components/BasePagination.vue'
import { useUserFormat } from '@/composables/useUserFormat'

const { t } = useI18n()
const { showSnackbar } = useSnackbar()
const router = useRouter()
const { getUserName, getUserPhone, formatUserName } = useUserFormat()

// Locales for date-fns
const locales = {
  en: enUS,
  ru: ru,
  vi: vi
}

// Format creation date
const formatCreatedAt = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return dateString
  
  if (isToday(date)) {
    return formatDistanceToNow(date, { 
      addSuffix: true,
      locale: locales[localStorage.getItem('locale') || 'en']
    })
  }
  return format(date, 'dd.MM.yyyy HH:mm', {
    locale: locales[localStorage.getItem('locale') || 'en']
  })
}





// Table state
const packages = ref([])
const loading = ref(false)
const page = ref(1)
const itemsPerPage = ref(10)
const totalItems = ref(0)
const search = ref('')
const selectedStatus = ref(null)
const selectedType = ref(null)
const sortBy = ref([])
const sortDesc = ref([])
const packageStatuses = ref([
  { title: 'new', value: 'new' },
  { title: 'locked', value: 'locked' },
  { title: 'finished', value: 'finished' },
  { title: 'canceled', value: 'canceled' },
  { title: 'expired', value: 'expired' }
])
const packageTypes = ref([
  { title: t('packages.types.delivery'), value: 'delivery' },
  { title: t('packages.types.storage'), value: 'storage' },
  { title: t('packages.types.shared'), value: 'shared' }
])

// Table headers
const headers = ref([
  { title: t('packages.device'), key: 'device_sn', sortable: true },
  { title: t('packages.cell'), key: 'cell_number', sortable: true },
  { title: t('packages.sender'), key: 'sender', sortable: true },
  { title: t('packages.recipient'), key: 'recipient', sortable: true },
  { title: t('packages.createdAt'), key: 'created_at', sortable: true },
  { title: t('packages.status'), key: 'status', sortable: true },
  { title: t('packages.type'), key: 'parcel_type', sortable: true }
])

// Fetch packages with filters
const fetchPackages = async () => {
  loading.value = true
  try {
    const params = {
      page: page.value,
      page_size: itemsPerPage.value,
      search: search.value,
      status: selectedStatus.value,
      parcel_type: selectedType.value
    }
    
    // Add sorting parameters
    if (sortBy.value.length > 0) {
      params.ordering = sortDesc.value[0] ? `-${sortBy.value[0]}` : sortBy.value[0]
    }
    
    const response = await api.get('/api/v1/parcel/', { params })
    
    packages.value = response.data.results
    totalItems.value = response.data.count
  } catch (error) {
    console.error('Failed to fetch packages:', error)
  } finally {
    loading.value = false
  }
}

// Debounced search
const debouncedSearch = debounce(() => {
  fetchPackages()
}, 300)

// Status styling
const getStatusClass = (status) => {
  return {
    'status-chip': true,
    'status-new': status === 'new',
    'status-locked': status === 'locked',
    'status-finished': status === 'finished',
    'status-canceled': status === 'canceled',
    'status-expired': status === 'expired'
  }
}

const getStatusColor = (status) => {
  const colors = {
    new: 'warning',
    locked: 'info',
    finished: 'success',
    canceled: 'grey-darken-1',
    expired: 'error'
  }
  return colors[status] || 'grey'
}

// Type styling
const getTypeClass = (type) => {
  return {
    'type-chip': true,
    'type-delivery': type === 'delivery',
    'type-storage': type === 'storage',
    'type-shared': type === 'shared'
  }
}

const getTypeColor = (type) => {
  const colors = {
    delivery: 'primary',
    storage: 'secondary',
    shared: 'info'
  }
  return colors[type] || 'grey'
}

// Copy to clipboard
const copyToClipboard = async (text, field) => {
  try {
    await navigator.clipboard.writeText(text)
    showSnackbar(t('common.copied', { field }))
  } catch (err) {
    showSnackbar(t('common.copyError'), 'error')
  }
}

// Navigate to package details
const navigateToPackageDetails = (event, { item }) => {
  router.push(`/packages/${item.order_uuid}`)
}

// Watch for changes in page and items per page
watch([page, itemsPerPage], () => {
  fetchPackages()
})

// Initialize
onMounted(async () => {
  await fetchPackages()
})
</script>

<style scoped>
.filters-wrapper {
  margin-bottom: 16px;
}

.table-search {
  width: 300px;
}

.table-filter {
  min-width: 150px;
}

.custom-table {
  border-radius: 8px;
  overflow: hidden;
}

.status-chip {
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.type-chip {
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.v-container {
  max-width: 1200px !important;
  margin-left: auto;
  margin-right: auto;
}

.user-info {
  line-height: 1.2;
}

.user-name-line {
  font-weight: 500;
  color: rgba(var(--v-theme-on-surface), 0.9);
  margin-bottom: 2px;
}

.user-phone-line {
  font-size: 0.875rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
  font-family: monospace;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 8px;
}
</style> 